// =====================================================
// COMPANY ADMIN PAGE - Datos de la Empresa
// =====================================================

import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  Building,
  Save,
  Loader2,
  AlertCircle,
  X,
  Users,
  MapPin,
  Phone,
  Mail,
  FileText,
  RefreshCw,
  Edit2,
  Building2,
  UserCheck,
} from 'lucide-react';
import { companyApi, type Company } from '../../api/company';
import { LogoUploader } from '../../components/ui/LogoUploader';
import { toast } from '../../components/ui/Toast';

interface CompanyFormData {
  name: string;
  taxId: string;
  address: string;
  phone: string;
  email: string;
}

export function CompanyAdminPage() {
  // Data state
  const [company, setCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Form
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CompanyFormData>();

  // Load data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await companyApi.get();
      setCompany(data);
      reset({
        name: data.name,
        taxId: data.taxId,
        address: data.address || '',
        phone: data.phone || '',
        email: data.email || '',
      });
      setLogoUrl(data.logoUrl || null);
    } catch (err: any) {
      setError(err.message || 'Error al cargar los datos de la empresa');
    } finally {
      setIsLoading(false);
    }
  }, [reset]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handlers
  const handleEdit = () => {
    setIsEditing(true);
    setFormError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormError(null);
    if (company) {
      reset({
        name: company.name,
        taxId: company.taxId,
        address: company.address || '',
        phone: company.phone || '',
        email: company.email || '',
      });
    }
  };

  const handleSave = async (formData: CompanyFormData) => {
    setFormError(null);
    setIsSaving(true);

    try {
      const updated = await companyApi.update({
        name: formData.name,
        taxId: formData.taxId,
        address: formData.address || undefined,
        phone: formData.phone || undefined,
        email: formData.email || undefined,
        logoUrl: logoUrl !== null ? logoUrl : null,
      });
      setCompany(updated);
      setIsEditing(false);
      toast.success('Guardado', 'Datos de la empresa actualizados');
    } catch (err: any) {
      setFormError(err.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl">
        <div className="flex items-center gap-3">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
          <button onClick={loadData} className="ml-auto text-sm font-medium underline">
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Empresa</h1>
          <p className="text-sm text-gray-500 mt-1">Datos y configuración de la empresa</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadData}
            disabled={isLoading}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Actualizar"
          >
            <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          {!isEditing && (
            <button
              onClick={handleEdit}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 shadow-md shadow-blue-100 transition-colors"
            >
              <Edit2 className="h-4 w-4" />
              Editar
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{company?.sitesCount || 0}</p>
              <p className="text-sm text-gray-500">Sedes activas</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Users className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{company?.usersCount || 0}</p>
              <p className="text-sm text-gray-500">Usuarios activos</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <UserCheck className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{company?.patientsCount || 0}</p>
              <p className="text-sm text-gray-500">Pacientes registrados</p>
            </div>
          </div>
        </div>
      </div>

      {/* Company Data Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Building className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Datos de la empresa</h2>
              <p className="text-sm text-gray-500">Información fiscal y de contacto</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(handleSave)}>
          {formError && (
            <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-600">{formError}</p>
              </div>
              <button type="button" onClick={() => setFormError(null)} className="text-red-400 hover:text-red-600">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="p-6 space-y-6">
            {/* Nombre y CIF */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Building className="h-4 w-4 inline mr-1.5 text-gray-400" />
                  Nombre de la empresa *
                </label>
                {isEditing ? (
                  <>
                    <input
                      type="text"
                      {...register('name', { required: 'El nombre es obligatorio' })}
                      className={`w-full px-4 py-2.5 border rounded-xl text-sm ${
                        errors.name ? 'border-red-300 bg-red-50/50' : 'border-gray-200'
                      }`}
                      placeholder="Nombre de la empresa"
                    />
                    {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 font-medium">{company?.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FileText className="h-4 w-4 inline mr-1.5 text-gray-400" />
                  CIF/NIF *
                </label>
                {isEditing ? (
                  <>
                    <input
                      type="text"
                      {...register('taxId', { required: 'El CIF es obligatorio' })}
                      className={`w-full px-4 py-2.5 border rounded-xl text-sm uppercase ${
                        errors.taxId ? 'border-red-300 bg-red-50/50' : 'border-gray-200'
                      }`}
                      placeholder="B12345678"
                    />
                    {errors.taxId && <p className="mt-1 text-xs text-red-500">{errors.taxId.message}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 font-medium">{company?.taxId}</p>
                )}
              </div>
            </div>

            {/* Dirección */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="h-4 w-4 inline mr-1.5 text-gray-400" />
                Dirección
              </label>
              {isEditing ? (
                <input
                  type="text"
                  {...register('address')}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
                  placeholder="Dirección completa"
                />
              ) : (
                <p className={company?.address ? 'text-gray-900' : 'text-gray-400 italic'}>
                  {company?.address || 'No especificada'}
                </p>
              )}
            </div>

            {/* Teléfono y Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="h-4 w-4 inline mr-1.5 text-gray-400" />
                  Teléfono
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    {...register('phone')}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
                    placeholder="+34 XXX XXX XXX"
                  />
                ) : (
                  <p className={company?.phone ? 'text-gray-900' : 'text-gray-400 italic'}>
                    {company?.phone || 'No especificado'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="h-4 w-4 inline mr-1.5 text-gray-400" />
                  Email
                </label>
                {isEditing ? (
                  <input
                    type="email"
                    {...register('email')}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
                    placeholder="empresa@ejemplo.com"
                  />
                ) : (
                  <p className={company?.email ? 'text-gray-900' : 'text-gray-400 italic'}>
                    {company?.email || 'No especificado'}
                  </p>
                )}
              </div>
            </div>
          </div>

            {/* Logo de la empresa */}
            <div className="mt-4">
              <LogoUploader
                currentLogoUrl={logoUrl}
                label="Logo de la empresa"
                hint="Aparece en la esquina superior izquierda de los presupuestos PDF"
                onChange={(url) => setLogoUrl(url)}
                disabled={!isEditing}
              />
            </div>

          {/* Actions */}
          {isEditing && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSaving}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Guardar cambios
                  </>
                )}
              </button>
            </div>
          )}
        </form>
      </div>

      {/* Info adicional */}
      {!isEditing && company && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium">Información</p>
              <p className="mt-1">
                Empresa creada el {new Date(company.createdAt).toLocaleDateString('es-ES', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}.
                Última actualización: {new Date(company.updatedAt).toLocaleDateString('es-ES', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyAdminPage;
